import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Music, Sparkles, Heart, GraduationCap, Radio, Instagram, Crown } from 'lucide-react';

const API = '/api';

const translations = {
  tr: {
    lessons: "Video Dersler",
    tuner: "Online Akort",
    wizard: "Saz Asistan\u0131",
    donate: "Destek Ol",
    pricing: "Fiyatlar",
    login: "Giri\u015f Yap",
    dashboard: "Panelim",
    home: "Ana Sayfa",
  },
  en: {
    lessons: "Lessons",
    tuner: "Tuner",
    wizard: "Saz Assistant",
    donate: "Support",
    pricing: "Pricing",
    login: "Login",
    dashboard: "Dashboard",
    home: "Home",
  },
};

export const MainNavigation = ({ lang = 'tr', setLang, user: propUser }) => {
  const [user, setUser] = useState(propUser || null);
  const navigate = useNavigate();
  const location = useLocation();
  const t = translations[lang] || translations.tr;

  useEffect(() => {
    const fetchUser = async () => {
      if (propUser) { setUser(propUser); return; }
      try {
        const token = localStorage.getItem('auth_token');
        const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
        const response = await fetch(`${API}/auth/me`, { credentials: 'include', headers });
        if (response.ok) {
          setUser(await response.json());
        } else {
          setUser(null);
          localStorage.removeItem('auth_token');
        }
      } catch { setUser(null); }
    };
    fetchUser();
  }, [propUser, location.pathname]);

  const handleLogout = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      await fetch(`${API}/auth/logout`, { method: 'POST', credentials: 'include', headers });
    } catch (e) { console.error('Logout error:', e); }
    localStorage.removeItem('auth_token');
    setUser(null);
    navigate('/');
  };

  const isActive = (path) => location.pathname === path;

  // Bottom tab items
  const bottomTabs = [
    { path: '/baglama-dersleri', label: t.lessons, icon: GraduationCap, testId: 'tab-lessons' },
    { path: '/baglama-akort', label: t.tuner, icon: Radio, testId: 'tab-tuner' },
    { path: '/satin-al', label: t.wizard, icon: Sparkles, testId: 'tab-wizard', isCenter: true },
    { path: '/bagis', label: t.donate, icon: Heart, testId: 'tab-donate' },
  ];

  return (
    <>
      {/* ===== Top Navigation Bar ===== */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-md border-b border-white/5" data-testid="top-nav">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-6">
          <div className="flex items-center justify-between h-14">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 flex-shrink-0" data-testid="logo-link">
              <div className="w-9 h-9 rounded-xl bg-primary flex items-center justify-center">
                <Music className="w-5 h-5 text-white" />
              </div>
              <span className="font-heading text-lg font-bold text-white hidden sm:inline">
                {"Ba\u011flama Sat\u0131n Al"}
              </span>
              <span className="font-heading text-sm font-bold text-white sm:hidden flex flex-col leading-tight">
                <span>{"Ba\u011flama"}</span>
                <span>{"Sat\u0131n Al"}</span>
              </span>
            </Link>

            {/* Desktop Nav Links */}
            <div className="hidden lg:flex items-center gap-1">
              {[
                { path: '/', label: t.home },
                { path: '/baglama-dersleri', label: t.lessons, icon: Crown },
                { path: '/baglama-akort', label: t.tuner },
                { path: '/fiyatlar', label: t.pricing },
              ].map(item => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${
                    isActive(item.path)
                      ? 'text-white bg-white/10'
                      : 'text-white/60 hover:text-white hover:bg-white/5'
                  }`}
                  data-testid={`nav-${item.path.replace(/\//g, '') || 'home'}`}
                >
                  {item.icon && <item.icon className="w-3.5 h-3.5 text-primary" />}
                  {item.label}
                </Link>
              ))}

              {/* Saz Asistani - Highlighted */}
              <Link
                to="/satin-al"
                className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-all flex items-center gap-1.5 ${
                  isActive('/satin-al')
                    ? 'bg-primary text-white'
                    : 'bg-primary/15 text-primary hover:bg-primary/25 border border-primary/30'
                }`}
                data-testid="nav-wizard"
              >
                <Sparkles className="w-3.5 h-3.5" />
                {t.wizard}
              </Link>

              {/* Destek Ol */}
              <Link
                to="/bagis"
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all flex items-center gap-1.5 ${
                  isActive('/bagis')
                    ? 'bg-pink-500/20 text-pink-300'
                    : 'text-pink-400/80 hover:text-pink-300 hover:bg-pink-500/10'
                }`}
                data-testid="nav-donate"
              >
                <Heart className="w-3.5 h-3.5" />
                {t.donate}
              </Link>
            </div>

            {/* Right side */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <a
                href="https://instagram.com/baglamasatinal"
                target="_blank"
                rel="noopener noreferrer"
                className="hidden sm:flex p-1.5 text-white/40 hover:text-pink-400 transition-colors"
                data-testid="instagram-link"
              >
                <Instagram className="w-4 h-4" />
              </a>

              {user ? (
                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => navigate('/dashboard')}
                    className="bg-primary hover:bg-primary/90 rounded-full px-4 py-1 text-xs h-8"
                    data-testid="dashboard-btn"
                  >
                    {t.dashboard}
                  </Button>
                  <Button
                    onClick={handleLogout}
                    variant="ghost"
                    className="text-white/50 hover:text-white rounded-full px-3 py-1 text-xs h-8 hidden sm:inline-flex"
                    data-testid="logout-btn"
                  >
                    {"\u00c7\u0131k\u0131\u015f"}
                  </Button>
                </div>
              ) : (
                <Button
                  onClick={() => navigate('/login')}
                  className="bg-primary hover:bg-primary/90 rounded-full px-4 py-1 text-xs h-8"
                  data-testid="login-btn"
                >
                  {t.login}
                </Button>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* ===== Bottom Tab Bar (visible on mobile/tablet) ===== */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-md border-t border-white/10" data-testid="bottom-tab-bar">
        <div className="flex items-end justify-around px-2 pb-[env(safe-area-inset-bottom)] h-16">
          {bottomTabs.map((tab) => {
            const active = isActive(tab.path);
            const Icon = tab.icon;

            if (tab.isCenter) {
              return (
                <Link
                  key={tab.path}
                  to={tab.path}
                  className="flex flex-col items-center -mt-4 relative"
                  data-testid={tab.testId}
                >
                  <div className={`w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-all ${
                    active
                      ? 'bg-primary shadow-primary/40'
                      : 'bg-gradient-to-br from-primary to-orange-600 shadow-primary/20'
                  }`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <span className={`text-[10px] mt-0.5 font-medium ${active ? 'text-primary' : 'text-white/60'}`}>
                    {tab.label}
                  </span>
                </Link>
              );
            }

            return (
              <Link
                key={tab.path}
                to={tab.path}
                className="flex flex-col items-center pt-2 pb-1 min-w-[60px]"
                data-testid={tab.testId}
              >
                <Icon className={`w-5 h-5 transition-colors ${
                  active ? (tab.path === '/bagis' ? 'text-pink-400' : 'text-primary') : 'text-white/40'
                }`} />
                <span className={`text-[10px] mt-1 font-medium transition-colors ${
                  active ? (tab.path === '/bagis' ? 'text-pink-400' : 'text-primary') : 'text-white/40'
                }`}>
                  {tab.label}
                </span>
                {active && (
                  <div className={`w-1 h-1 rounded-full mt-0.5 ${tab.path === '/bagis' ? 'bg-pink-400' : 'bg-primary'}`} />
                )}
              </Link>
            );
          })}
        </div>
      </div>

      {/* Bottom spacer for mobile tab bar */}
      <div className="lg:hidden h-20" />
    </>
  );
};

export default MainNavigation;
